import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ChevronDown, Settings } from "lucide-react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { ServiceResumoDesk } from "@/services/serviceResumoDesk";
import { useLayout } from "@/contexts/UseLayout";
import {
  GerenciarTarefasRapidasModal,
  getTaskIconComponent,
} from "./GerenciarTarefasRapidasModal";

interface TarefaRapidaDropdownProps {
  type: string;
  data: any;
  departamentoActive: number;
  onTaskCreated: () => void;
}

export function TarefaRapidaDropdown({
  type,
  data,
  departamentoActive,
  onTaskCreated,
}: TarefaRapidaDropdownProps) {
  const { t } = useTranslation();
  const context = useLayout();
  const queryClient = useQueryClient();
  const taskType = type === "recebimento" ? "pagamento" : type;
  const [isOpen, setIsOpen] = useState(false);
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [gerenciarOpen, setGerenciarOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
  }>({ left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { data: templatesData, isLoading } = useQuery({
    queryKey: ["tarefas-templates"],
    queryFn: async () => {
      const res = await ServiceResumoDesk.listTarefasTemplates();
      return res?.data ?? null;
    },
    staleTime: 1000 * 60 * 2,
  });

  const DROPDOWN_HEIGHT_EST = 320;
  const updatePosition = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < DROPDOWN_HEIGHT_EST;
    setDropdownPos(
      openUpward
        ? {
            bottom: window.innerHeight - rect.top + 8,
            left: rect.left,
            width: Math.max(rect.width, 288),
          }
        : {
            top: rect.bottom + 8,
            left: rect.left,
            width: Math.max(rect.width, 288),
          },
    );
  };

  const openDropdown = () => {
    updatePosition();
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClose = (e: MouseEvent) => {
      const target = e.target as Node;
      const panel = document.getElementById("tarefa-rapida-portal-panel");
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        panel &&
        !panel.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    const handleReposition = () => updatePosition();
    document.addEventListener("mousedown", handleClose);
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      document.removeEventListener("mousedown", handleClose);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [isOpen]);

  const handleSelectTemplate = async (template: any) => {
    if (loadingId !== null) return;
    try {
      setLoadingId(template.id);
      setIsOpen(false);

      const followUpDate = moment()
        .add(1, "day")
        .startOf("day")
        .add(9, "hours")
        .format("YYYY-MM-DDTHH:mm");
      const description = template.description
        ? template.description
        : template.title;

      const payload: any = {
        type: taskType,
        id_departamento: departamentoActive,
        status: 1,
        is_public: true,
        title: template.title,
        description,
        date: followUpDate,
        priority: 2,
        id_responsavel: context?.userLogged?.data?.id,
      };

      if (taskType === "desk") payload.id_desk = data.id;
      if (taskType === "reunion") payload.id_reunion = data.id;
      if (taskType === "prospecting") payload.id_prospecting = data.id;
      if (taskType === "pagamento") payload.id_pagamento = data.id;
      if (taskType === "campanha") payload.id_campanha = data.id;

      const res = await ServiceResumoDesk.addTasks(payload);
      if (res?.status === 200) {
        toast.success(t("tasks.quick_task.success"));
        onTaskCreated();
      } else {
        toast.error(t("tasks.quick_task.error"));
      }
    } catch {
      toast.error(t("tasks.quick_task.error"));
    } finally {
      setLoadingId(null);
    }
  };

  const handleGerenciarOpen = () => {
    setIsOpen(false);
    setGerenciarOpen(true);
  };

  const templates: any[] = templatesData?.data ?? templatesData ?? [];

  const dropdownPanel = isOpen
    ? createPortal(
        <div
          id="tarefa-rapida-portal-panel"
          style={{
            position: "fixed",
            top: dropdownPos.top,
            bottom: dropdownPos.bottom,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 9999,
          }}
          className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 pt-4 pb-2">
            <p className="text-sm font-semibold text-[#1E1E1E]">
              {t("tasks.quick_task.title")}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {t("tasks.quick_task.subtitle")}
            </p>
          </div>

          <div className="px-2 pb-2">
            {isLoading && (
              <div className="flex flex-col gap-1 px-2 py-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-12 rounded-xl bg-gray-100 animate-pulse"
                  />
                ))}
              </div>
            )}

            {!isLoading && templates.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-3 px-2">
                {t("tasks.quick_task.no_templates")}
              </p>
            )}

            {!isLoading &&
              templates.map((tmpl: any) => (
                <button
                  key={tmpl.id}
                  type="button"
                  disabled={loadingId === tmpl.id}
                  onClick={() => handleSelectTemplate(tmpl)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left disabled:opacity-60"
                >
                  <div
                    className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white"
                    style={{ backgroundColor: tmpl.color ?? "#3B82F6" }}
                  >
                    {getTaskIconComponent(tmpl.icon_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1E1E1E] leading-tight">
                      {tmpl.title}
                    </p>
                    {tmpl.description && (
                      <p className="text-xs text-gray-400 truncate leading-tight mt-0.5">
                        {tmpl.description}
                      </p>
                    )}
                  </div>
                </button>
              ))}
          </div>

          {/* Divider + Manage */}
          <div className="border-t border-gray-100">
            <button
              type="button"
              onClick={handleGerenciarOpen}
              className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-500 hover:bg-gray-50 transition-colors"
            >
              <Settings size={15} />
              {t("tasks.quick_task.manage")}
            </button>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => (isOpen ? setIsOpen(false) : openDropdown())}
        disabled={loadingId !== null}
        className="bg-white flex items-center justify-center text-azul-400 gap-2 py-2.5 px-4 rounded-full border border-azul-400 hover:bg-indigo-50 duration-300 disabled:opacity-50 select-none w-full sm:w-auto whitespace-nowrap"
      >
        <span className="font-medium text-sm">
          {t("tasks.quick_task.button")}
        </span>
        <ChevronDown
          size={16}
          className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {dropdownPanel}

      <GerenciarTarefasRapidasModal
        open={gerenciarOpen}
        onOpenChange={(v) => {
          setGerenciarOpen(v);
          if (!v) {
            queryClient.invalidateQueries({ queryKey: ["tarefas-templates"] });
          }
        }}
      />
    </>
  );
}
